import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type SyntheticEvent } from 'react'

import { useSession } from '../session/SessionProvider.js'
import '../styles/forms.css'

/**
 * L'apertura di un tesseramento per una persona che ce l'ha già davanti.
 *
 * È un modulo di tre campi e sta dentro la scheda invece che su una schermata
 * propria: aprire un tesseramento è la conseguenza di aver appena guardato la
 * persona, e mandare altrove per tornare indietro subito dopo sarebbe un giro
 * inutile.
 *
 * Il numero di tessera **non** è proposto dal client. Il formato lo decide
 * ogni associazione — «2026/0001», «A-1234», la numerazione continua dal
 * registro cartaceo — e un valore suggerito male è peggio di nessun valore:
 * si accetta senza guardarlo.
 */
export function MembershipForm({
  personaId,
  onFatto,
  onAnnulla,
}: {
  personaId: number
  onFatto: () => void
  onAnnulla: () => void
}) {
  const { resources } = useSession()
  const cache = useQueryClient()
  const [violazioni, setViolazioni] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)

  const crea = useMutation({
    mutationFn: (corpo: { person: string; member_number: string; joined_on?: string }) =>
      resources.memberships.create(corpo),
    onMutate: () => {
      setErrore(null)
      setViolazioni({})
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['memberships'] })
      onFatto()
    },
    onError: (e: unknown) => {
      const problema = e as {
        status?: number
        violations?: readonly { propertyPath: string; message: string }[]
      }

      if (problema.status === 422 && problema.violations) {
        setViolazioni(
          Object.fromEntries(problema.violations.map((v) => [v.propertyPath, v.message])),
        )

        return
      }

      setErrore(
        problema.status === 409
          ? 'Questo numero di tessera è già usato.'
          : 'Non è stato possibile aprire il tesseramento.',
      )
    },
  })

  function invia(evento: SyntheticEvent<HTMLFormElement>) {
    evento.preventDefault()

    const campi = new FormData(evento.currentTarget)
    const dal = ((campi.get('joined_on') as string | null) ?? '').trim()

    crea.mutate({
      person: `/api/v1/people/${String(personaId)}`,
      member_number: ((campi.get('member_number') as string | null) ?? '').trim(),
      // La data assente si **omette**, non si manda a `null`: `joined_on` non
      // è annullabile e `null` produce un `400` che parla di stringhe vuote.
      // Omettendola decide il server, che la mette a oggi — l'unica lettura
      // che non inventa una data del passato.
      ...(dal === '' ? {} : { joined_on: dal }),
    })
  }

  return (
    <form className="modulo modulo--breve" onSubmit={invia}>
      <fieldset>
        <legend>Nuovo tesseramento</legend>

        {errore && (
          <p role="alert" className="messaggio messaggio--errore">
            {errore}
          </p>
        )}

        <label className="modulo__campo">
          <span>
            Numero di tessera <abbr title="obbligatorio">*</abbr>
          </span>
          <input
            type="text"
            name="member_number"
            aria-invalid={violazioni['member_number'] ? true : undefined}
          />
          {violazioni['member_number'] && (
            <span className="modulo__errore" role="alert">
              {violazioni['member_number']}
            </span>
          )}
        </label>

        <label className="modulo__campo">
          <span>Iscritto dal</span>
          <input type="date" name="joined_on" />
        </label>

        <div className="modulo__azioni">
          <button type="button" data-variante="piatto" onClick={onAnnulla} disabled={crea.isPending}>
            Annulla
          </button>
          <button type="submit" disabled={crea.isPending}>
            {crea.isPending ? 'Apertura…' : 'Apri il tesseramento'}
          </button>
        </div>
      </fieldset>
    </form>
  )
}
